import { useState } from 'react';

interface FilterState {
  pet: string[];
  functionCategory: string[];
  material: string[];
  priceMin: number;
  priceMax: number;
  pricingTier: string[];
}

interface FilterSidebarProps {
  filters: FilterState;
  onTogglePet: (id: string) => void;
  onToggleFunction: (id: string) => void;
  onToggleMaterial: (id: string) => void;
  onToggleTier: (id: string) => void;
  onPriceChange: (min: number, max: number) => void;
  onClearAll: () => void;
}

const PET_FILTERS = [
  {
    id: 'dog',
    label: 'Dog',
    subcategories: [
      { id: 'puppy', label: 'Puppy' },
      { id: 'adult-moderate', label: 'Adult – Moderate Chewer' },
      { id: 'adult-aggressive', label: 'Adult – Aggressive Chewer' },
      { id: 'senior', label: 'Senior' },
    ],
  },
  {
    id: 'cat',
    label: 'Cat',
    subcategories: [
      { id: 'kitten-adult', label: 'Kitten / Adult' },
    ],
  },
  {
    id: 'universal',
    label: 'Universal Accessories',
    subcategories: [],
  },
];

const FUNCTION_FILTERS = [
  { id: 'chew-dental', label: 'Chew & Dental' },
  { id: 'enrichment-puzzle', label: 'Enrichment & Puzzle' },
  { id: 'fetch-active', label: 'Fetch & Active Play' },
  { id: 'comfort-snuggle', label: 'Comfort & Snuggle' },
  { id: 'everyday-accessories', label: 'Everyday Accessories' },
  { id: 'gifting-edits', label: 'Gifting Edits' },
];

const MATERIAL_FILTERS = [
  { id: 'natural-rubber', label: 'Natural Rubber' },
  { id: 'jute-hemp', label: 'Jute / Hemp' },
  { id: 'coconut-coir', label: 'Coconut Coir' },
  { id: 'bamboo', label: 'Bamboo' },
  { id: 'organic-cotton', label: 'Organic Cotton' },
  { id: 'silicone', label: 'Silicone' },
];

const TIER_FILTERS = [
  { id: 'essentials', label: 'Essentials', range: 'Under Rs.400' },
  { id: 'signature', label: 'Signature', range: 'Rs.400 – 799' },
  { id: 'premium-enrichment', label: 'Premium Enrichment', range: 'Rs.800 – 1,199' },
  { id: 'gifting-sets', label: 'Gifting Sets', range: 'Rs.1,200+' },
];

const PRICE_MIN = 249;
const PRICE_MAX = 1599;

export default function FilterSidebar({
  filters,
  onTogglePet,
  onToggleFunction,
  onToggleMaterial,
  onToggleTier,
  onPriceChange,
  onClearAll,
}: FilterSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    pet: true,
    function: true,
    material: false,
    price: false,
    tier: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const hasActiveFilters =
    filters.pet.length > 0 ||
    filters.functionCategory.length > 0 ||
    filters.material.length > 0 ||
    filters.priceMin !== PRICE_MIN ||
    filters.priceMax !== PRICE_MAX ||
    filters.pricingTier.length > 0;

  const checkIcon = (
    <i className="ri-check-line text-xs"></i>
  );

  return (
    <div className="text-foreground-800">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-lg font-bold text-foreground-900">
          <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
            <i className="ri-equalizer-line text-accent-500"></i>
          </span>
          Filters
        </h3>
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors duration-150 whitespace-nowrap cursor-pointer"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-1">
        {/* Pet Filter */}
        <div className="border-b border-background-200/70 pb-1">
          <button
            onClick={() => toggleSection('pet')}
            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-700 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
          >
            <span>Pet</span>
            <span className="w-4 h-4 flex items-center justify-center">
              {expandedSections.pet ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </span>
          </button>
          {expandedSections.pet && (
            <div className="pb-3 space-y-1">
              {PET_FILTERS.map(petGroup => (
                <div key={petGroup.id}>
                  <label className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
                    <div
                      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all duration-150 ${
                        filters.pet.includes(petGroup.id)
                          ? 'bg-accent-500 border-accent-500 text-background-50'
                          : 'border-foreground-300 group-hover:border-accent-400'
                      }`}
                    >
                      {filters.pet.includes(petGroup.id) && checkIcon}
                    </div>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={filters.pet.includes(petGroup.id)}
                      onChange={() => onTogglePet(petGroup.id)}
                    />
                    <span className="text-sm text-foreground-700 group-hover:text-foreground-900 transition-colors duration-150">
                      {petGroup.label}
                    </span>
                  </label>
                  {petGroup.subcategories.length > 0 && (
                    <div className="ml-6 space-y-0.5">
                      {petGroup.subcategories.map(sub => (
                        <label key={sub.id} className="flex items-center gap-2.5 py-1 cursor-pointer group">
                          <div
                            className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all duration-150 ${
                              filters.pet.includes(sub.id)
                                ? 'bg-accent-500 border-accent-500 text-background-50'
                                : 'border-foreground-300 group-hover:border-accent-400'
                              }`}
                          >
                            {filters.pet.includes(sub.id) && checkIcon}
                          </div>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={filters.pet.includes(sub.id)}
                            onChange={() => onTogglePet(sub.id)}
                          />
                          <span className="text-xs text-foreground-500 group-hover:text-foreground-700 transition-colors duration-150">
                            {sub.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Function Filter */}
        <div className="border-b border-background-200/70 pb-1">
          <button
            onClick={() => toggleSection('function')}
            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-700 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
          >
            <span>Function</span>
            <span className="w-4 h-4 flex items-center justify-center">
              {expandedSections.function ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </span>
          </button>
          {expandedSections.function && (
            <div className="pb-3 space-y-0.5">
              {FUNCTION_FILTERS.map(func => (
                <label key={func.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all duration-150 ${
                      filters.functionCategory.includes(func.id)
                        ? 'bg-accent-500 border-accent-500 text-background-50'
                        : 'border-foreground-300 group-hover:border-accent-400'
                      }`}
                  >
                    {filters.functionCategory.includes(func.id) && checkIcon}
                  </div>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={filters.functionCategory.includes(func.id)}
                    onChange={() => onToggleFunction(func.id)}
                  />
                  <span className="text-sm text-foreground-600 group-hover:text-foreground-900 transition-colors duration-150">
                    {func.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Material Filter */}
        <div className="border-b border-background-200/70 pb-1">
          <button
            onClick={() => toggleSection('material')}
            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-700 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
          >
            <span>Material</span>
            <span className="w-4 h-4 flex items-center justify-center">
              {expandedSections.material ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </span>
          </button>
          {expandedSections.material && (
            <div className="pb-3 space-y-0.5">
              {MATERIAL_FILTERS.map(mat => (
                <label key={mat.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all duration-150 ${
                      filters.material.includes(mat.id)
                        ? 'bg-accent-500 border-accent-500 text-background-50'
                        : 'border-foreground-300 group-hover:border-accent-400'
                      }`}
                  >
                    {filters.material.includes(mat.id) && checkIcon}
                  </div>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={filters.material.includes(mat.id)}
                    onChange={() => onToggleMaterial(mat.id)}
                  />
                  <span className="text-sm text-foreground-600 group-hover:text-foreground-900 transition-colors duration-150">
                    {mat.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Price Range Filter */}
        <div className="border-b border-background-200/70 pb-1">
          <button
            onClick={() => toggleSection('price')}
            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-700 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
          >
            <span>Price Range</span>
            <span className="w-4 h-4 flex items-center justify-center">
              {expandedSections.price ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </span>
          </button>
          {expandedSections.price && (
            <div className="pb-4">
              <div className="relative h-10 flex items-center">
                <div className="absolute inset-x-0 h-2 bg-background-200 rounded-full" />
                <div
                  className="absolute h-2 bg-accent-400 rounded-full"
                  style={{
                    left: `${((filters.priceMin - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
                    right: `${100 - ((filters.priceMax - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100}%`,
                  }}
                />
                <input
                  type="range"
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  value={filters.priceMin}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (val <= filters.priceMax) {
                      onPriceChange(val, filters.priceMax);
                    }
                  }}
                  className="absolute inset-x-0 w-full h-2 appearance-none bg-transparent pointer-events-auto cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background-50 [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-accent-500 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-background-50 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:relative [&::-moz-range-thumb]:z-10"
                  style={{ zIndex: filters.priceMin > PRICE_MAX - 100 ? 5 : 0 }}
                  aria-label="Minimum price"
                />
                <input
                  type="range"
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  value={filters.priceMax}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (val >= filters.priceMin) {
                      onPriceChange(filters.priceMin, val);
                    }
                  }}
                  className="absolute inset-x-0 w-full h-2 appearance-none bg-transparent pointer-events-auto cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background-50 [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-accent-500 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-background-50 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:relative [&::-moz-range-thumb]:z-10"
                  aria-label="Maximum price"
                />
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs font-semibold text-foreground-700 bg-background-100 px-2.5 py-1 rounded-md">
                  Rs.{filters.priceMin}
                </span>
                <span className="text-xs text-foreground-400">–</span>
                <span className="text-xs font-semibold text-foreground-700 bg-background-100 px-2.5 py-1 rounded-md">
                  Rs.{filters.priceMax}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Tier Filter */}
        <div>
          <button
            onClick={() => toggleSection('tier')}
            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-700 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
          >
            <span>Pricing Tier</span>
            <span className="w-4 h-4 flex items-center justify-center">
              {expandedSections.tier ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </span>
          </button>
          {expandedSections.tier && (
            <div className="pb-3 space-y-0.5">
              {TIER_FILTERS.map(tier => (
                <label key={tier.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all duration-150 ${
                      filters.pricingTier.includes(tier.id)
                        ? 'bg-accent-500 border-accent-500 text-background-50'
                        : 'border-foreground-300 group-hover:border-accent-400'
                      }`}
                  >
                    {filters.pricingTier.includes(tier.id) && checkIcon}
                  </div>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={filters.pricingTier.includes(tier.id)}
                    onChange={() => onToggleTier(tier.id)}
                  />
                  <span className="text-sm text-foreground-600 group-hover:text-foreground-900 transition-colors duration-150">
                    {tier.label}
                  </span>
                  <span className="text-[11px] text-foreground-400 ml-auto">{tier.range}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}